/**
 * The commands this surface may send, and the idempotency rules around them.
 *
 * Core keys every public command on a `command_id` and replays a stored result
 * when the same id arrives with the same payload (`commandResult` /
 * `storeCommandResult`). That is what makes a double-submitted checkout safe,
 * so the browser's job is to reuse an id for a *retry of the same intent* and
 * to mint a new one when the intent changes. Both halves matter: reusing an id
 * after the customer edits their address would make core reject the edit as an
 * idempotency conflict, and minting a new id on a double-click would create a
 * second quote and a second reservation attempt.
 *
 * There is no auto-retry here. Design Bible 13.2 is explicit that the
 * storefront never auto-retries a payment; every command is attempted once and
 * hands its outcome back with a stated next action.
 */

import {
  CHECKOUT_QUOTES_PATH,
  CUSTOMER_SESSION_PATH,
  INQUIRIES_PATH,
  checkoutContinuePath
} from "./coreRoutes.js";
import {
  parseCheckoutStarted,
  parseContinuation,
  parseCustomerSession,
  parseInquiryReceipt,
  type CheckoutStarted,
  type ContinuationReceipt,
  type CustomerSessionReceipt,
  type InquiryReceipt
} from "./publicContract.js";
import { type Outcome, type Transport, correlationID, interpret } from "./transport.js";
import type { RestrictedAddressCommand } from "./address.js";

export const COMMERCE_SCHEMA_VERSION = "commerce.v1";
/** Core's `COMMERCE_MAX_ITEMS`. A cart cannot exceed it, so neither can a command. */
export const MAX_CART_ITEMS = 20;
/** Core's `COMMERCE_MAX_INQUIRY_BYTES`. */
export const MAX_INQUIRY_BYTES = 2_000;

export const inquiryCategories = ["ITEM_QUESTION", "ORDER_HELP", "GENERAL"] as const;
export type InquiryCategory = (typeof inquiryCategories)[number];

/**
 * A command id. Core bounds it at 200 characters and rejects control
 * characters; 32 hex characters from the same generator the correlation id uses
 * is comfortably inside that and carries no customer information.
 */
export function commandID(random: () => number = Math.random): string {
  return `${correlationID(random)}${correlationID(random)}`;
}

export interface CheckoutStartInput {
  readonly commandID: string;
  readonly itemIDs: readonly string[];
  /** Present for a guest; absent when core will identify the customer by cookie. */
  readonly guestEmail?: string;
  readonly shippingOptionID: string;
  readonly shippingMinor: number;
  readonly insuranceMinor: number;
  readonly address: RestrictedAddressCommand;
}

/**
 * `POST /api/core/checkout/quotes`.
 *
 * Core recomputes prices, discounts, and the collector tier from its own
 * records; nothing in this body is trusted as money. `shipping_minor` and
 * `insurance_minor` are sent because core requires them to match the configured
 * shipping option exactly — they are an assertion about which option was shown,
 * not a price the browser is choosing.
 */
export async function startCheckout(
  transport: Transport,
  input: CheckoutStartInput,
  csrfToken?: string
): Promise<Outcome<CheckoutStarted>> {
  if (input.itemIDs.length < 1 || input.itemIDs.length > MAX_CART_ITEMS) {
    return { kind: "rejected", detail: `A checkout holds between 1 and ${String(MAX_CART_ITEMS)} items.` };
  }
  const reply = await transport({
    path: CHECKOUT_QUOTES_PATH,
    method: "POST",
    correlationID: correlationID(),
    ...(csrfToken === undefined ? {} : { csrfToken }),
    body: {
      schema_version: COMMERCE_SCHEMA_VERSION,
      command_id: input.commandID,
      item_ids: [...input.itemIDs],
      ...(input.guestEmail === undefined ? {} : { guest_email: input.guestEmail }),
      shipping_option_id: input.shippingOptionID,
      shipping_minor: input.shippingMinor,
      insurance_minor: input.insuranceMinor,
      address: input.address
    }
  });
  return interpret(reply, parseCheckoutStarted, "a valid checkout session");
}

/**
 * `POST /api/core/checkout/orders/{id}/continue`.
 *
 * This is the entire browser callback. It asks core to *retrieve* the
 * authoritative provider result and returns a queued job id. It cannot and does
 * not report payment: MASTER_SPEC 9.6 and Design Bible 13.3 both require the
 * browser's return from PayPal to be a request for the truth rather than a
 * claim about it.
 */
export async function continueCheckout(
  transport: Transport,
  orderID: string,
  commandIDValue: string,
  credentials: { readonly csrfToken?: string; readonly checkoutToken?: string }
): Promise<Outcome<ContinuationReceipt>> {
  const reply = await transport({
    path: checkoutContinuePath(orderID),
    method: "POST",
    correlationID: correlationID(),
    ...(credentials.csrfToken === undefined ? {} : { csrfToken: credentials.csrfToken }),
    ...(credentials.checkoutToken === undefined ? {} : { checkoutToken: credentials.checkoutToken }),
    body: { command_id: commandIDValue }
  });
  return interpret(reply, parseContinuation, "the checkout this order belongs to");
}

export interface InquiryInput {
  readonly commandID: string;
  readonly physicalItemID?: string;
  readonly guestEmail?: string;
  readonly category: InquiryCategory;
  readonly message: string;
}

/** `POST /api/core/inquiries`. Rate-limited server-side; 429 is a real state. */
export async function sendInquiry(
  transport: Transport,
  input: InquiryInput,
  csrfToken?: string
): Promise<Outcome<InquiryReceipt>> {
  const bytes = new TextEncoder().encode(input.message).length;
  if (bytes < 1 || bytes > MAX_INQUIRY_BYTES) {
    return {
      kind: "rejected",
      detail: `A message is between 1 and ${String(MAX_INQUIRY_BYTES)} bytes.`
    };
  }
  const reply = await transport({
    path: INQUIRIES_PATH,
    method: "POST",
    correlationID: correlationID(),
    ...(csrfToken === undefined ? {} : { csrfToken }),
    body: {
      schema_version: COMMERCE_SCHEMA_VERSION,
      command_id: input.commandID,
      ...(input.physicalItemID === undefined ? {} : { physical_item_id: input.physicalItemID }),
      ...(input.guestEmail === undefined ? {} : { guest_email: input.guestEmail }),
      category: input.category,
      message: input.message
    }
  });
  return interpret(reply, parseInquiryReceipt, "a valid inquiry");
}

/**
 * `POST /api/core/customer/sessions`.
 *
 * Core requires an authenticated `customers` record on the request and answers
 * with a CSRF token, setting `lp_customer_session` as a Secure HttpOnly cookie.
 * This surface therefore never holds a bearer token and cannot create a session
 * on its own — the sign-in step itself belongs to core, which is why the
 * account view links to it rather than posting credentials from here.
 */
export async function openCustomerSession(
  transport: Transport
): Promise<Outcome<CustomerSessionReceipt>> {
  const reply = await transport({
    path: CUSTOMER_SESSION_PATH,
    method: "POST",
    correlationID: correlationID()
  });
  return interpret(reply, parseCustomerSession, "a signed-in collector account");
}
